import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../../utils'

import { AccentHighlight } from './AccentHighlight'

interface SecondaryGradientBorderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  className?: string
  innerClassName?: string
}

const SecondaryGradientBorder = React.forwardRef<HTMLDivElement, SecondaryGradientBorderProps>(
  ({ children, className, innerClassName, ...props }, ref) => {
    return (
      <div
        className={cn(
          'rounded-full bg-linear-to-r from-[#B0C8DF80] via-[#6E99C2] to-[#B0C8DF80] p-px relative',
          className,
        )}
        ref={ref}
        {...props}
      >
        <div className={cn('h-full w-full flex items-center justify-center rounded-full bg-[#0b0b0b]', innerClassName)}>
          {children}
        </div>
      </div>
    )
  },
)

SecondaryGradientBorder.displayName = 'SecondaryGradientBorder'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 rounded-full w-fit font-normal',
  {
    defaultVariants: {
      size: 'medium',
      variant: 'default',
    },
    variants: {
      size: {
        large: 'py-3.5 px-7 text-base',
        medium: ' py-2 px-6 text-base',
        small: 'p-1.5 px-2 xs:py-2 xs:px-5 text-sm gap-1',
      },
      variant: {
        default: 'bg-[#FAFAFA] border border-[#3C4149] text-black hover:bg-[#FAFAFA]/80',
        error: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        secondary: 'relative text-[#FAFAFA] hover:text-[#FAFAFA]/70',
        text: 'text-[#FAFAFA] hover:text-[#FAFAFA]/70',
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild = false, className, size, variant, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    if (variant === 'secondary') {
      return (
        <SecondaryGradientBorder>
          {asChild ? (
            <Comp className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
          ) : (
            <Comp className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props}>
              {props.children}
              <AccentHighlight className="h-px bottom-[0.5px] w-[60%] left-1/2 -translate-x-1/2" />
            </Comp>
          )}
        </SecondaryGradientBorder>
      )
    }

    return (
      <Comp className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props}>
        {props.children}
      </Comp>
    )
  },
)

Button.displayName = 'Button'

export { Button, buttonVariants, SecondaryGradientBorder }
